import { useContext, useEffect, useState, useRef } from "react";
import { ConfigContext } from "../../util/ConfigContext";
import { useQuery } from "@tanstack/react-query";
import {
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Squares2X2Icon, StopIcon } from "@heroicons/react/24/solid"; // Heroicons
import { fetchMeetings, fetchSchedule } from "../../util/fetchUserUtil";
import {
  FilterSkeleton,
  MeetingSkeleton,
  MatchCardSkeleton,
} from "../Skeletons";
import { isTokenValid } from "../../util/JwtUtil";
import dayjs from "dayjs";
// import { toast } from "react-toastify";
import { apiGet } from "../../util/axiosUtil";
import { useNavigate } from "react-router-dom";
import { logoutWithRedirect } from "../../util/JwtUtil";
import LoadingOverlay from "../LoadingOverlay";
// import { getJwtData } from "../../util/JwtUtil";
import API_URL from "../../config/apiConfig";
import { ZoomMtg } from "@zoom/meetingsdk";
import { Button } from "@headlessui/react";
import LiveScoreCard from "../LiveScoreCard";
import detectedPlatform from "../../util/PlatformUtils";
import {
  fakeData,
  generateWebToken,
  genarateAndroidToken,
  generateIosToken,
} from "../../config/fakeUserConfig";
import "../../zoom.css";
import UpcomingMatchesCard from "../UpcomingMatchesCard";

const ScheduleWithZoom = () => {
  const { jwt, rawJwt } = useContext(ConfigContext);
  const [filterBy, setFilterBy] = useState("null");
  const [zoomError, setZoomError] = useState(null);
  const [zoomLoading, setZoomLoading] = useState(false);
  const [isUserEligibleForMeeting, setIsUserEligibleForMeeting] =
    useState(false);
  const [isFilteredReady, setIsFilteredReady] = useState(false);
  const [filteredSchedule, setFilteredSchedule] = useState([]);
  const [detectPlatform, setdetectPlatform] = useState("web");
  const [isFakeUser, setIsFakeUser] = useState(null);
  const [isMultiScreenOn, setIsMultiScreenOn] = useState(null);
  // const [liveMatches, setLiveMatches] = useState([]);
  const socketRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isShuttingDownRef = useRef(false);
  const isReplacedByDeviceRef = useRef(false); // ← ADD THIS LINE
  const reconnectTimerRef = useRef(null);
  const isMountedRef = useRef(true);
  const websocketUrlRef = useRef(null);
  const detectPlatformRef = useRef("web");

  // const liveSocketRef = useRef(null);
  // const liveDataReconnectAttemptsRef = useRef(0);
  // const allowReconnectRef = useRef(true);
  const videoSendingUsers = new Set();
  let currentAudioStatus = true;
  const leaveUrl = `${window.location.origin}/schedule`;
  const protocol = window.location.protocol === "https:" ? "wss" : "ws";
  const navigate = useNavigate();
  const host =
    window.location.protocol === "https:"
      ? window.location.hostname
      : `${window.location.hostname}${process.env.REACT_APP_BACKEND_PORT}`;

  useEffect(() => {
    setdetectPlatform(detectedPlatform());
    if (
      detectedPlatform() === "android" &&
      window?.ZoomHelper?.isAndroidTv?.()
    ) {
      const timeout = setTimeout(() => {
        const button = document.querySelector("#firstMeeting");
        button?.focus();
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, []);

  // Keeps detectPlatformRef in sync so unmount cleanup reads latest value
  useEffect(() => {
    detectPlatformRef.current = detectPlatform;
  }, [detectPlatform]);

  useEffect(() => {
    const checkParam = async () => {
      if (!rawJwt || !jwt || !isTokenValid(jwt)) {
        logoutWithRedirect(null, jwt?.tenant);
      }
    };
    checkParam();
  }, [rawJwt, jwt]);

  useEffect(() => {
    if (jwt?.un) {
      const isFake = fakeData.Users.includes(jwt.un);
      setIsFakeUser(isFake);
    }
  }, [jwt]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Cancel any pending reconnect timer
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      // Close socket directly via ref — avoids stale closure issue
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, []);

  // ✅ Only runs once on mount and unmount
  // useEffect(() => {
  //   return () => {
  //     console.log(
  //       "🧹 Component actually unmounted — closing live data socket..."
  //     );
  //     if (isFakeUser !== null && !isFakeUser) {
  //       closeLiveDataSocket();
  //     }
  //   };
  // }, []); // no dependencies

  // useEffect(() => {
  //   if (isFakeUser !== null && !isFakeUser) {
  //     if (allowReconnectRef.current) {
  //       console.log("🔗 Connecting live data socket...");
  //       connectLiveDataSocket();
  //     } else {
  //       console.log("🔌 JWT invalid or missing — closing live data socket...");
  //       closeLiveDataSocket();
  //     }
  //   }
  // }, [isFakeUser]);

  useEffect(() => {
    const eligibleRoles = ["OWNER", "MANAGER", "ADMIN", "SUBSCRIBER", "HOST"];
    // const allowedMultiScreenTenants = ["multiapp", "multi", "royal", "royaltv"];
    const allowedMultiScreenTenants = ["royal", "royaltv", "cricsports"];
    setIsUserEligibleForMeeting(eligibleRoles.includes(jwt?.role));
    setIsMultiScreenOn(
      allowedMultiScreenTenants.includes(jwt?.tenant?.toLowerCase()),
    );
  }, [jwt]);

  useEffect(() => {
    window.onMeetingStatusChanged = (status, platform) => {
      // console.log("Received from native:", status, platform);
      if (platform === "ios") {
        if (status === 0 || status === 4 || status === 6 || status === 7) {
          setZoomLoading(false);
        }
      } else if (platform === "android" || platform === "windows") {
        if (
          status === "MEETING_STATUS_IDLE" ||
          status === "MEETING_STATUS_FAILED" ||
          status === "MEETING_STATUS_DISCONNECTING"
        ) {
          setZoomLoading(false);
        }
      }
    };

    window.logoutUser = () => {
      leaveMeetingCleanup();
      logoutWithRedirect(null, jwt?.tenant);
    };

    return () => {
      delete window.onMeetingStatusChanged;
      delete window.logoutUser;
      const overlay = document.getElementById("custom-overlay");
      if (overlay) overlay.remove();
    };
  }, []);

  const {
    data: schedule,
    isLoading: isScheduleLoading,
    isError: isScheduleFetchError,
    error: scheduleFetchError,
  } = useQuery({
    queryKey: ["schedule"],
    queryFn: fetchSchedule,
    refetchOnWindowFocus: false,
    enabled: !!rawJwt && isFakeUser === false, // ✅ only run for non-fake users
    retry: false, // 🚫 disable retries completely
    onError: (err) => {
      console.error("Failed to fetch Schedule:", err);
    },
  });

  const {
    data: meetings,
    isLoading: isMeetingsLoading,
    isError: isMeetingError,
    error: meetingFetchError,
  } = useQuery({
    queryKey: ["hostMeetings"],
    queryFn: fetchMeetings,
    refetchOnWindowFocus: false,
    enabled: isUserEligibleForMeeting && !isFakeUser,
    retry: false, // 🚫 disable retries completely
    onError: (err) => {
      console.error("Failed/d to fetch meetings:", err);
    },
  });

  useEffect(() => {
    if (isFakeUser) {
      const upcomingMatches = fakeData.schedule?.upcoming || [];
      const liveMatches = fakeData.schedule?.live || [];

      const filteredUpcoming =
        filterBy === "null"
          ? upcomingMatches
          : upcomingMatches.filter((game) => game.GAME_TYPE === filterBy);

      setFilteredSchedule({
        upcoming: filteredUpcoming,
        live: liveMatches, // keep live as is
      });

      setIsFilteredReady(true);
    } else if (schedule?.data) {
      const upcomingMatches = schedule.data.upcoming || [];
      const liveMatches = schedule.data.live || [];

      const filteredUpcoming =
        filterBy === "null"
          ? upcomingMatches
          : upcomingMatches.filter((game) => game.GAME_TYPE === filterBy);

      setFilteredSchedule({
        upcoming: filteredUpcoming,
        live: liveMatches, // no filtering
      });

      setIsFilteredReady(true);
    } else {
      setFilteredSchedule({ upcoming: [], live: [] });
      setIsFilteredReady(false);
    }
  }, [schedule, filterBy, isFakeUser]);

  useEffect(() => {
    return () => {
      if (detectPlatformRef.current === "web") {
        // ← ref instead of stale closure
        leaveMeetingCleanup();
      }
    };
  }, []);

  const handleJoinMeeting = async (meetingId, isDefaultUIMode) => {
    if (!isFakeUser) {
      setZoomLoading(true);
      const authEndpoint = `${API_URL}/api/zooms/get-zoom-token/${meetingId}`;
      try {
        const response = await apiGet(authEndpoint);
        const jsonObject = JSON.parse(atob(response.data));
        startMeeting(jsonObject, response.data, isDefaultUIMode);
      } catch (err) {
        setZoomLoading(false);
        setZoomError(
          err?.response?.data?.data?.errorMessage ||
            err?.response?.data?.message ||
            "Failed to Generate Zoom Token.",
        );
        console.error(err?.response?.data?.data?.errorMessage);
      }
    } else {
      setZoomLoading(true);
      if (detectPlatform === "web") {
        const generatedMeetingData = await generateWebToken(meetingId, jwt.un);
        startMeeting(generatedMeetingData, null, true);
      } else if (detectPlatform === "android") {
        const { rawJson, base64 } = await genarateAndroidToken(
          meetingId,
          jwt.un,
        );
        startMeeting(rawJson, base64, isDefaultUIMode);
      } else if (detectPlatform === "ios") {
        const { rawJson, base64 } = await generateIosToken(meetingId, jwt.un);
        startMeeting(rawJson, base64);
      }
    }
  };

  // const leaveMeetingCleanup = () => {
  //   return new Promise((resolve) => {
  //     if (detectPlatform === "web" && typeof ZoomMtg !== "undefined") {
  //       try {
  //         ZoomMtg.leaveMeeting({
  //           success: () => {
  //             ZoomMtg.reRender({
  //               success: () => {
  //                 document.getElementById("root").style.display = "";
  //                 document.getElementById("zmmtg-root")?.remove();

  //                 // cleanup app state
  //                 reconnectAttemptsRef.current = 10;
  //                 allowReconnectRef.current = false;
  //                 closeSocket();
  //                 setZoomError(null);

  //                 resolve(); // ✅ resolve ONLY after Zoom cleanup
  //               },
  //               error: (err) => {
  //                 console.warn("⚠️ Re-render error:", err);
  //                 resolve(); // still resolve to avoid blocking logout
  //               },
  //             });
  //           },
  //           error: (err) => {
  //             console.error("❌ Error leaving Zoom:", err);
  //             resolve(); // resolve even on error
  //           },
  //         });
  //       } catch (e) {
  //         console.log("⚠️ No active Zoom meeting or leaveMeeting failed");
  //         resolve();
  //       }
  //     } else {
  //       // Non-web or Zoom not loaded
  //       reconnectAttemptsRef.current = 10;
  //       allowReconnectRef.current = false;
  //       closeSocket();
  //       setZoomError(null);
  //       resolve();
  //     }
  //   });
  // };
  const leaveMeetingCleanup = (calledFrom4001 = false) => {
    return new Promise((resolve) => {
      if (detectPlatform === "web" && typeof ZoomMtg !== "undefined") {
        try {
          ZoomMtg.leaveMeeting({
            success: () => {
              ZoomMtg.reRender({
                success: () => {
                  document.getElementById("root").style.display = "";
                  document.getElementById("zmmtg-root")?.remove();
                  setZoomError(null);
                  if (!calledFrom4001) {
                    closeSocket(); // ← safe, manual leave only
                  }
                  resolve();
                },
                error: (err) => {
                  console.warn("Re-render error:", err);
                  resolve();
                },
              });
            },
            error: (err) => {
              console.error("Error leaving Zoom:", err);
              resolve();
            },
          });
        } catch (e) {
          console.warn("No active Zoom meeting or leaveMeeting failed");
          resolve();
        }
      } else {
        setZoomError(null);
        if (!calledFrom4001) {
          closeSocket(); // ← safe, manual leave only
        }
        resolve();
      }
    });
  };

  const startMeeting = (jsonData, encodedString, isDefaultUIMode) => {
    // allowReconnectRef.current = false;
    // closeLiveDataSocket();
    const websocketUrl = `${protocol}://${host}/ws?token=${rawJwt}`;
    if (typeof ZoomHelper !== "undefined") {
      console.log("Zoom detected for start meeting");
      // eslint-disable-next-line no-undef
      ZoomHelper.joinMeeting(encodedString, websocketUrl, isDefaultUIMode);
      setdetectPlatform("android");
      return;
    }

    if (window?.nativeBridge?.send) {
      window.nativeBridge.send({
        encodedString: encodedString,
        websocketUrl: websocketUrl,
      });
      setdetectPlatform("windows");
      return;
    }

    if (window?.chrome?.webview?.postMessage) {
      window.chrome.webview.postMessage({
        encodedString: encodedString,
        websocketUrl: websocketUrl,
      });
      setdetectPlatform("windows");
      return;
    }

    // eslint-disable-next-line no-undef
    if (typeof webkit !== "undefined" && webkit?.messageHandlers?.callback) {
      // eslint-disable-next-line no-undef
      webkit.messageHandlers.callback.postMessage(
        JSON.stringify({
          encodedString: encodedString,
          websocketUrl: websocketUrl,
        }),
      );
      setdetectPlatform("ios");
      return;
    }

    document.getElementById("root").style.display = "none";
    ZoomMtg.init({
      leaveUrl,
      disableCORP: true,
      patchJsMedia: true,
      leaveOnPageUnload: true,
      showMeetingHeader: false,
      disablePreview: true,
      isLockBottom: true,
      isSupportChat: true,
      isSupportAV: true,
      success: () => {
        ZoomMtg.preLoadWasm();
        ZoomMtg.prepareWebSDK();
        ZoomMtg.inMeetingServiceListener("onMeetingStatus", function (status) {
          if (status.meetingStatus === 2) setZoomLoading(false);
          populateInitialVideoUsers();
        });
        ZoomMtg.join({
          signature: jsonData.web.signature,
          sdkKey: jsonData.web.sdkKey,
          meetingNumber: jsonData.web.meetingNumber,
          passWord: jsonData.web.passWord,
          userName: jsonData.web.userName,
          userEmail: jsonData.web.userEmail,
          success: () => {
            const overlay = document.createElement("div");
            overlay.id = "custom-overlay";
            overlay.innerHTML = `
              <div id="drag-container" style="
                position: fixed;
                bottom: 20px;
                right: 20px;
                z-index: 9999;
                background: rgba(0, 0, 0, 0.6);
                color: white;
                font-family: sans-serif;
                border-radius: 10px;
                box-shadow: 0 4px 12px rgba(0,0,0,0.3);
                overflow: hidden;
                display: flex;
                flex-direction: column;
                align-items: center;
              ">
                <div id="drag-handle" style="
                  width: 100%;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  padding: 6px 10px;
                  cursor: move;
                  background: rgba(255, 255, 255, 0.1);
                  font-size: 14px;
                  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                ">
                  🖐️ Drag
                </div>
                <div style="display: flex; flex-direction: column; padding: 10px;">
                  <button id="next-btn" title="Next" style="background: transparent; border: none; padding: 6px; cursor: pointer;">
                    <img src="/assets/ic_next.png" alt="Next" width="28" height="28" />
                  </button>
                  <button id="audio-btn" title="Toggle Audio" style="background: transparent; border: none; padding: 6px; cursor: pointer;">
                    <img id="audio-icon" src="/assets/ic_speaker.png" alt="Audio" width="28" height="28" />
                  </button>
                  <button id="fullscreen-btn" title="Fullscreen" style="background: transparent; border: none; padding: 6px; cursor: pointer;">
                    <img id="fullscreen-icon" src="/assets/ic_fullscreen_enter.png" alt="Enter Fullscreen" width="28" height="28" />
                  </button>
                  <button id="leave-btn" title="Leave Meeting" style="background: transparent; border: none; padding: 6px; cursor: pointer;">
                    <img src="/assets/ic_power.png" alt="Leave" width="28" height="28" />
                  </button>
                </div>
              </div>
            `;
            document.body.appendChild(overlay);

            setTimeout(() => {
              document
                .getElementById("drag-handle")
                ?.classList.remove("animate-drag-hint");
            }, 2000);

            const dragContainer = document.getElementById("drag-container");
            const dragHandle = document.getElementById("drag-handle");
            let isDragging = false;
            let offsetX = 0;
            let offsetY = 0;

            dragHandle.addEventListener("mousedown", (e) => {
              isDragging = true;
              const rect = dragContainer.getBoundingClientRect();
              offsetX = e.clientX - rect.left;
              offsetY = e.clientY - rect.top;
              dragContainer.style.transition = "none";
            });

            document.addEventListener("mousemove", (e) => {
              if (isDragging) {
                const containerWidth = dragContainer.offsetWidth;
                const containerHeight = dragContainer.offsetHeight;
                const viewportWidth = window.innerWidth;
                const viewportHeight = window.innerHeight;
                let newLeft = e.clientX - offsetX;
                let newTop = e.clientY - offsetY;
                newLeft = Math.max(
                  0,
                  Math.min(viewportWidth - containerWidth, newLeft),
                );
                newTop = Math.max(
                  0,
                  Math.min(viewportHeight - containerHeight, newTop),
                );
                dragContainer.style.left = `${newLeft}px`;
                dragContainer.style.top = `${newTop}px`;
                dragContainer.style.right = "auto";
              }
            });

            document.addEventListener("mouseup", () => {
              isDragging = false;
            });

            document
              .getElementById("leave-btn")
              .addEventListener("click", () => {
                leaveMeetingCleanup();
              });

            document
              .getElementById("audio-btn")
              .addEventListener("click", () => {
                toggleListenAudio();
              });

            document
              .getElementById("next-btn")
              .addEventListener("click", () => {
                handleNextButton();
              });

            document
              .getElementById("fullscreen-btn")
              .addEventListener("click", toggleFullscreen);

            document.addEventListener("fullscreenchange", updateFullscreenIcon);
            document.addEventListener(
              "webkitfullscreenchange",
              updateFullscreenIcon,
            );
            document.addEventListener(
              "msfullscreenchange",
              updateFullscreenIcon,
            );

            ZoomMtg.inMeetingServiceListener("onUserJoin", function (data) {});
            ZoomMtg.inMeetingServiceListener("onUserLeave", function (data) {});
            ZoomMtg.inMeetingServiceListener(
              "onActiveSpeaker",
              function (data) {
                const activeUserId = data.userId;
                ZoomMtg.getAttendeeslist({
                  success(res) {
                    const user = res.result.attendeesList.find(
                      (u) => u.userId === activeUserId,
                    );
                    if (user) {
                      alert(`🗣️ Active Speaker: ${user.userName}`);
                    }
                  },
                });
              },
            );
            ZoomMtg.inMeetingServiceListener(
              "onNetworkQualityChange",
              function (data) {},
            );
            ZoomMtg.inMeetingServiceListener(
              "onReceiveChatMsg",
              function (data) {},
            );
            ZoomMtg.inMeetingServiceListener("onUserUpdate", function (data) {
              const { userId, video } = data;
              if (video) {
                videoSendingUsers.add(userId);
              } else {
                videoSendingUsers.delete(userId);
              }
            });
            connectSocket(websocketUrl);
          },
          error: (err) => {
            console.error("Join Error:", err);
            setZoomError("Could not join meeting.");
            setZoomLoading(false);
          },
        });
      },
      error: (err) => {
        document.getElementById("root").style.display = "";
        document.getElementById("zmmtg-root")?.remove();
        setZoomLoading(false);
        setZoomError("Could not initialize Zoom: " + err.message);
      },
    });
  };

  function updateFullscreenIcon() {
    const fullscreenIcon = document.getElementById("fullscreen-icon");
    const fullscreenBtn = document.getElementById("fullscreen-btn");
    const isNowFullscreen =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement;
    if (fullscreenIcon) {
      fullscreenIcon.src = isNowFullscreen
        ? "/assets/ic_fullscreen_exit.png"
        : "/assets/ic_fullscreen_enter.png";
      fullscreenIcon.alt = isNowFullscreen
        ? "Exit Fullscreen"
        : "Enter Fullscreen";
    }
    if (fullscreenBtn) {
      fullscreenBtn.title = isNowFullscreen
        ? "Exit Fullscreen"
        : "Enter Fullscreen";
    }
  }

  function toggleFullscreen() {
    const elem = document.documentElement;
    if (
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement
    ) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    } else {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      }
    }
  }

  function showToast(message) {
    const toast = document.createElement("div");
    toast.innerText = message;
    toast.style.position = "fixed";
    toast.style.bottom = "30px";
    toast.style.right = "30px";
    toast.style.background = "rgba(0,0,0,0.8)";
    toast.style.color = "#fff";
    toast.style.padding = "10px 16px";
    toast.style.borderRadius = "8px";
    toast.style.fontSize = "14px";
    toast.style.zIndex = "9999";
    toast.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.3)";
    toast.style.transition = "opacity 0.5s ease";
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.opacity = "0";
      setTimeout(() => toast.remove(), 500);
    }, 3000);
  }

  function toggleListenAudio() {
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);
    if (isMobile) {
      showToast("🚫 This feature is not supported on mobile devices.");
      return;
    }
    ZoomMtg.getCurrentUser({
      success: (res) => {
        let currentUserData = res.result.currentUser;
        if (!currentUserData.audio || currentUserData.audio === "off") {
          console.warn("You're not connected to audio.");
          return;
        }
        ZoomMtg.stopIncomingAudio({
          stop: currentAudioStatus,
          success: () => {
            const icon = currentAudioStatus
              ? "/assets/ic_speaker_mute.png"
              : "/assets/ic_speaker.png";
            const audioIcon = document.getElementById("audio-icon");
            if (audioIcon) {
              audioIcon.src = icon;
              audioIcon.alt = currentAudioStatus
                ? "Speaker Muted"
                : "Speaker On";
            }
            currentAudioStatus = !currentAudioStatus;
          },
          error: (err) => {
            console.error("❌ Failed to toggle speaker:", err);
          },
        });
      },
      error: (err) => {
        console.error("Failed to get current user:", err.message);
      },
    });
  }

  function handleNextButton() {
    const videoSendingUsersArray = Array.from(videoSendingUsers);
    if (videoSendingUsersArray.length === 0) {
      return;
    }
    ZoomMtg.getPinList({
      success: function (res) {
        console.log("Pinned user IDs for me:", res.result);
        let userToPin = videoSendingUsersArray[0];
        if (res.result.length !== 0) {
          const currentIndex = videoSendingUsersArray.indexOf(
            res.result.pinVideoList[0],
          );
          const nextIndex = (currentIndex + 1) % videoSendingUsersArray.length;
          userToPin = videoSendingUsersArray[nextIndex];
        }
        ZoomMtg.operatePin({
          operate: "replace",
          userId: userToPin,
          success: () => {},
          error: (err) => {
            console.error("❌ Failed to pin user:", err);
          },
        });
      },
      error: function (err) {
        console.error("getPinList failed", err);
      },
    });
  }

  function populateInitialVideoUsers() {
    ZoomMtg.getAttendeeslist({
      success: function (res) {
        const attendees = res.result.attendeesList;
        attendees.forEach((user) => {
          if (user.video) {
            ZoomMtg.operatePin({
              operate: "replace",
              userId: user.userId,
              success: () => {},
              error: (err) => {
                console.error("❌ Failed to pin user:", err);
              },
            });
            videoSendingUsers.add(user.userId);
          }
        });
      },
      error: function (err) {
        console.error("❌ Failed to fetch attendee list:", err);
      },
    });
  }

  // function sendchatMessage(userId) {
  //   ZoomMtg.sendChat({
  //     message: "Hello!",
  //     toJid: userId,
  //   });
  // }

  // function sendchatMessageToAll() {
  //   ZoomMtg.sendChat({
  //     message: "Hello everyone!",
  //   });
  // }

  // const connectSocket = (websocketUrl) => {
  //   if (socketRef.current) return;
  //   const ws = new WebSocket(websocketUrl);
  //   ws.onopen = () => {
  //     reconnectAttemptsRef.current = 0;
  //   };
  //   ws.onclose = (event) => {
  //     socketRef.current = null;
  //     if (event.code === 4001) {
  //       (async () => {
  //         console.log("Meeting Cleanup Called!");
  //         await leaveMeetingCleanup(); // ✅ wait until Zoom is fully closed
  //         logoutWithRedirect(navigate, jwt?.tenant);
  //       })();
  //       return;
  //     }
  //     if (reconnectAttemptsRef.current < 5) {
  //       reconnectAttemptsRef.current++;
  //       setTimeout(
  //         () => connectSocket(websocketUrl),
  //         1000 * reconnectAttemptsRef.current,
  //       );
  //     }
  //   };
  //   ws.onerror = (err) => {
  //     console.error("WebSocket Error", err);
  //     ws.close();
  //   };
  //   socketRef.current = ws;
  // };

  // const closeSocket = () => {
  //   if (socketRef.current) {
  //     console.log("Socket close called");
  //     socketRef.current.close();
  //     socketRef.current = null;
  //   }
  // };

  const connectSocket = (websocketUrl) => {
    if (socketRef.current) return; // ← only block if already connected

    // Reset shutdown flag — a new intentional connection is starting
    isShuttingDownRef.current = false;
    isReplacedByDeviceRef.current = false;

    websocketUrlRef.current = websocketUrl;
    const ws = new WebSocket(websocketUrl);
    // ← NEW: handle REPLACED_BY_NEW_DEVICE signal from backend
    // This fires BEFORE onclose, so isShuttingDownRef is true
    // by the time onclose runs — no reconnect possible
    ws.onmessage = (event) => {
      if (event.data === "REPLACED_BY_NEW_DEVICE") {
        isReplacedByDeviceRef.current = true; // ← specific flag only
      }
    };

    ws.onopen = () => {
      reconnectAttemptsRef.current = 0;
    };

    ws.onclose = (event) => {
      socketRef.current = null;

      // Case 1: Replaced by another device
      // Either caught early via onmessage, or fallback via event.code
      if (event.code === 4001 || isReplacedByDeviceRef.current) {
        isReplacedByDeviceRef.current = false; // reset after reading
        isShuttingDownRef.current = true; // block any reconnect
        (async () => {
          try {
            console.log("Replaced by new device — cleaning up");
            await leaveMeetingCleanup(true);
            logoutWithRedirect(navigate, jwt?.tenant);
          } catch (err) {
            console.error("Cleanup error:", err);
            logoutWithRedirect(navigate, jwt?.tenant);
          }
        })();
        return;
      }

      // Case 2: Manual close or unmount — no reconnect, no logout
      if (isShuttingDownRef.current || !isMountedRef.current) return;

      // Case 3: Unintended disconnect — reconnect with backoff
      if (reconnectAttemptsRef.current < 5) {
        reconnectAttemptsRef.current++;
        const delay = 1000 * reconnectAttemptsRef.current;
        console.log(
          `Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`,
        );
        reconnectTimerRef.current = setTimeout(
          () => connectSocket(websocketUrl),
          delay,
        );
      } else {
        console.error("Max reconnect attempts reached.");
      }
    };

    ws.onerror = (err) => {
      console.error("WebSocket Error", err);
      // ← Do NOT call ws.close() — browser fires onclose automatically
    };

    socketRef.current = ws;
  };

  const closeSocket = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    reconnectAttemptsRef.current = 0;
    if (socketRef.current) {
      console.log("closeSocket called — closing cleanly");
      isShuttingDownRef.current = true; // block onclose from reconnecting
      socketRef.current.close();
      socketRef.current = null;
      // ← REMOVED: isShuttingDownRef.current = false
      // Reason: ws.close() triggers onclose ASYNCHRONOUSLY
      // resetting the flag here meant onclose saw false and reconnected
    }
  };

  // const connectLiveDataSocket = () => {
  //   const SocketUrl = `${protocol}://${host}/ws/live?token=${rawJwt}`;

  //   // 🧩 Prevent multiple open sockets
  //   if (
  //     liveSocketRef.current &&
  //     (liveSocketRef.current.readyState === WebSocket.OPEN ||
  //       liveSocketRef.current.readyState === WebSocket.CONNECTING)
  //   ) {
  //     console.log(
  //       "⚠️ Live socket already open or connecting, skipping new connection."
  //     );
  //     return;
  //   }

  //   const ws = new WebSocket(SocketUrl);
  //   liveSocketRef.current = ws; // assign immediately

  //   ws.onopen = () => {
  //     liveDataReconnectAttemptsRef.current = 0;
  //     console.log("✅ Live Data WebSocket connected");
  //   };

  //   ws.onmessage = (event) => {
  //     try {
  //       const message = JSON.parse(event.data);

  //       switch (message.type) {
  //         // 🟢 INITIAL FULL DATA LOAD
  //         case "live_matches": {
  //           const parsedMatches = message.data.map((item) =>
  //             typeof item === "string" ? JSON.parse(item) : item
  //           );
  //           console.log("✅ Received initial matches:", parsedMatches);
  //           setLiveMatches(parsedMatches);
  //           break;
  //         }

  //         // 🔄 SINGLE MATCH UPDATED OR NEW
  //         case "match_update": {
  //           const matchId = message.match_id;
  //           const update = message.data;
  //           setLiveMatches((prevMatches) => {
  //             const exists = prevMatches.some(
  //               (m) => String(m.match_id) === String(matchId)
  //             );
  //             return exists
  //               ? prevMatches.map((m) =>
  //                   String(m.match_id) === String(matchId) ? update : m
  //                 )
  //               : [...prevMatches, update];
  //           });
  //           console.log("🔄 Updated/added match:", matchId);
  //           break;
  //         }

  //         // ❌ MATCH REMOVED / FINISHED
  //         case "match_removed": {
  //           const matchId = message.match_id;
  //           setLiveMatches((prevMatches) =>
  //             prevMatches.filter((m) => String(m.match_id) !== String(matchId))
  //           );
  //           console.log("🗑️ Removed match:", matchId);
  //           break;
  //         }

  //         case "error":
  //           console.error("❌ Server Error:", message.message);
  //           break;

  //         default:
  //           console.warn("⚠️ Unknown message type:", message);
  //       }
  //     } catch (err) {
  //       console.error("❌ Error parsing WebSocket message:", err, event.data);
  //     }
  //   };

  //   ws.onclose = (event) => {
  //     console.warn("⚠️ Live Data WebSocket disconnected", event);

  //     // 🛑 Handle token expiry or invalid token
  //     if (event.code === 4001 || event.reason === "Invalid token") {
  //       console.warn("❌ Token expired or invalid, logging out...");
  //       liveDataReconnectAttemptsRef.current = 0;
  //       allowReconnectRef.current = false;
  //       closeLiveDataSocket();
  //       logoutWithRedirect(navigate, jwt?.tenant);
  //       return;
  //     }

  //     liveSocketRef.current = null;

  //     // 🚫 If reconnects not allowed, stop here
  //     if (!allowReconnectRef.current) {
  //       console.log("🛑 Reconnect disabled (probably logged out)");
  //       return;
  //     }

  //     // 🔁 Normal reconnect flow
  //     if (liveDataReconnectAttemptsRef.current < 5) {
  //       liveDataReconnectAttemptsRef.current++;
  //       const delay = 1000 * liveDataReconnectAttemptsRef.current;
  //       console.log(
  //         `🔁 Attempting reconnect #${
  //           liveDataReconnectAttemptsRef.current
  //         } in ${delay / 1000}s...`
  //       );
  //       setTimeout(() => {
  //         if (allowReconnectRef.current) {
  //           connectLiveDataSocket();
  //         } else {
  //           console.log("🛑 Reconnect aborted — user logged out.");
  //         }
  //       }, delay);
  //     } else {
  //       console.warn("❌ Max reconnect attempts reached, giving up.");
  //     }
  //   };

  //   ws.onerror = (err) => {
  //     console.error("❌ WebSocket Error:", err);
  //     ws.close(); // triggers onclose handler above
  //   };
  // };

  // const closeLiveDataSocket = () => {
  //   if (liveSocketRef.current) {
  //     try {
  //       console.log("🔌 Closing live data socket...");
  //       liveSocketRef.current.close(1000, "Client logout or cleanup");
  //     } catch (err) {
  //       console.error("⚠️ Error while closing socket:", err);
  //     } finally {
  //       liveSocketRef.current = null;
  //     }
  //   }
  // };

  if (!rawJwt && !isTokenValid(jwt)) {
    console.log("No rawJWT or valid Token");
    return <LoadingOverlay type={1} />;
  }

  if (zoomError)
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm px-4">
        <div className="relative w-full max-w-md rounded-2xl border border-white/20 bg-white/10 dark:bg-gray-900/20 backdrop-blur-xl p-6 shadow-2xl text-center">
          {/* Close Button */}
          <button
            onClick={() => setZoomError(null)}
            className="absolute top-3 right-3 rounded-full p-1 text-white/70 hover:text-white hover:bg-white/10 transition"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>

          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="rounded-full bg-red-500/20 p-3">
              <ExclamationTriangleIcon className="h-10 w-10 text-red-400" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-xl font-semibold text-white mb-2">
            Failed to Join Meeting
          </h2>

          {/* Message */}
          <p className="text-white/80 text-sm leading-relaxed">
            {zoomError || "Error loading Zoom Meeting."}
          </p>
        </div>
      </div>
    );

  return (
    <>
      <div className="flex justify-center items-center min-h-screen relative">
        {zoomLoading && <LoadingOverlay type={3} />}

        <div
          className="w-full lg:w-9/12 p-4 lg:rounded-lg shadow-lg lg:mt-10 relative z-0 mb-0 lg:mb-16 bg-slate-900 min-h-screen"
          style={{
            backgroundImage: `url("./assets/back-pattern.png")`,
            backgroundRepeat: "repeat",
            backgroundSize: "auto",
          }}
        >
          {isUserEligibleForMeeting && (
            <>
              {isFakeUser ? (
                fakeData.meetings?.length > 0 ? (
                  detectedPlatform() === "android" ? (
                    <div className="flex flex-col items-center gap-4 mb-4">
                      {fakeData.meetings.map((meeting, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <button
                            id="firstMeeting"
                            onClick={() => handleJoinMeeting(idx, true)}
                            className="bg-orange-500 text-white font-bold py-2 px-6 rounded-l-full 
                             hover:bg-orange-700 
                             focus:bg-orange-600 focus:ring-2 focus:ring-orange-300 focus:outline-none 
                             transition"
                          >
                            DSA Batch 46
                          </button>
                          {isMultiScreenOn && (
                            <>
                              <div className="bg-white text-gray-700 font-bold px-2 py-1 rounded-full border border-gray-300 text-sm shadow-sm">
                                OR
                              </div>
                              <button
                                onClick={() => handleJoinMeeting(idx, false)}
                                className="bg-teal-500 text-white font-bold py-2 px-6 rounded-r-full 
                                hover:bg-teal-600 
                                focus:bg-teal-700 focus:ring-2 focus:ring-teal-300 focus:outline-none 
                                transition"
                              >
                                DSA Batch 46
                              </button>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-wrap justify-center gap-4 mb-4">
                      {fakeData.meetings.map((meeting, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleJoinMeeting(idx, true)}
                          className="bg-blue-500 text-white font-bold py-2 px-6 rounded-lg 
                           hover:bg-blue-600 
                           focus:bg-blue-700 focus:ring-2 focus:ring-blue-300 focus:outline-none 
                           transition"
                        >
                          DSA Batch 46
                        </button>
                      ))}
                    </div>
                  )
                ) : (
                  <div className="flex items-center justify-center gap-2 text-red-600 bg-red-50 dark:bg-red-950 px-4 py-2 rounded-md text-sm font-medium border border-red-300 dark:border-red-700">
                    <ExclamationTriangleIcon className="h-5 w-5" />
                    <span>No Meetings Configured</span>
                  </div>
                )
              ) : meetings?.data?.buttons.length > 0 ? (
                detectedPlatform() === "android" && meetings.data.video ? (
                  <div className="flex flex-col items-center gap-4 mb-4">
                    {meetings.data.buttons.map((meeting, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <button
                          id="firstMeeting"
                          onClick={() => handleJoinMeeting(meeting.id, true)}
                          className="
                            flex items-center justify-center gap-2
                            bg-orange-500 text-white font-semibold
                            rounded-l-full transition
                            hover:bg-orange-600 focus:bg-orange-700 focus:ring-2 focus:ring-orange-300 focus:outline-none

                            /* Padding adjusts with screen size */
                            px-3 py-2 text-xs
                            md:px-5 md:py-2.5 md:text-base
                            lg:px-6 lg:py-3 lg:text-lg
                          "
                        >
                          <StopIcon
                            className="
                            w-4 h-4
                            sm:w-5 sm:h-5
                            md:w-6 md:h-6
                          "
                          />
                          <span className="truncate max-w-[100px] sm:max-w-[150px] md:max-w-[200px] lg:max-w-[250px]">
                            {meeting.name}
                          </span>
                        </button>
                        {isMultiScreenOn && (
                          <>
                            <div className="bg-white text-gray-700 font-bold px-2 py-1 rounded-full border border-gray-300 text-sm shadow-sm">
                              OR
                            </div>
                            <button
                              onClick={() =>
                                handleJoinMeeting(meeting.id, false)
                              }
                              className="
                          flex items-center justify-center gap-2
                          bg-teal-500 text-white font-semibold
                          rounded-r-full transition
                          hover:bg-teal-600 focus:bg-teal-700 focus:ring-2 focus:ring-teal-300 focus:outline-none

                          /* Responsive spacing and font size */
                          px-3 py-2 text-xs
                          md:px-5 md:py-2.5 md:text-base
                          lg:px-6 lg:py-3 lg:text-lg
                        "
                            >
                              <Squares2X2Icon
                                className="
                            w-4 h-4
                            sm:w-5 sm:h-5
                            md:w-6 md:h-6
                          "
                              />
                              <span className="truncate max-w-[100px] sm:max-w-[150px] md:max-w-[200px] lg:max-w-[250px]">
                                {meeting.name}
                              </span>
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-wrap justify-center gap-3 sm:gap-4 md:gap-6 mb-4 px-2">
                    {meetings.data.buttons.map((meeting, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleJoinMeeting(meeting.id, true)}
                        className="flex items-center justify-center bg-blue-500 text-white font-semibold py-2 px-5 sm:px-6 md:px-8 rounded-lg 
                        hover:bg-blue-600 
                        focus:bg-blue-700 focus:ring-2 focus:ring-blue-300 focus:outline-none 
                        transition-all duration-200 
                        text-sm sm:text-base md:text-lg 
                        min-w-[120px] sm:min-w-[150px]"
                      >
                        {meeting.name}
                      </button>
                    ))}
                  </div>
                )
              ) : isMeetingError ? (
                <div className="flex items-center justify-center gap-2 text-red-600 bg-red-50 dark:bg-red-950 px-4 py-2 rounded-md text-sm font-medium border border-red-300 dark:border-red-700">
                  <ExclamationTriangleIcon className="h-5 w-5" />
                  <span>
                    {meetingFetchError?.response?.data?.data?.errorMessage?.replace(
                      "Unexpected runtime error occurred -> ",
                      "",
                    ) ||
                      meetingFetchError?.response?.data?.message ||
                      "Error loading Meetings."}
                  </span>
                </div>
              ) : isMeetingsLoading ? (
                <MeetingSkeleton count={2} />
              ) : (
                <div className="flex items-center justify-center gap-2 text-red-600 bg-red-50 dark:bg-red-950 px-4 py-2 rounded-md text-sm font-medium border border-red-300 dark:border-red-700">
                  <ExclamationTriangleIcon className="h-5 w-5" />
                  <span>No Meetings Found</span>
                </div>
              )}
              <hr className="my-4" />
            </>
          )}

          {!isFakeUser &&
            (!isScheduleLoading && !isScheduleFetchError && isFilteredReady ? (
              <div
                className="
                flex flex-wrap justify-center gap-2 sm:gap-3 md:gap-4 lg:gap-5
                my-2 sm:my-3 md:my-4
              "
              >
                {["null", "T20", "ODI", "TEST"].map((type) => (
                  <Button
                    key={type}
                    onClick={() => setFilterBy(type)}
                    className={`
                    rounded-lg text-white font-semibold transition focus:outline-none focus:ring-2

                    /* Responsive padding and font size */
                    px-3 py-1 text-xs
                    sm:px-4 sm:py-2 sm:text-sm
                    md:px-5 md:py-2 md:text-base
                    lg:px-6 lg:py-2 lg:text-lg

                    ${
                      filterBy === type
                        ? "bg-red-500 hover:bg-red-600 focus:bg-red-700 focus:ring-red-300"
                        : "bg-blue-500 hover:bg-blue-600 focus:bg-blue-700 focus:ring-blue-300"
                    }
                  `}
                  >
                    {type === "null" ? "All" : type}
                  </Button>
                ))}
              </div>
            ) : (
              !isScheduleFetchError && <FilterSkeleton />
            ))}

          {isScheduleLoading ? (
            <div className="text-white text-center mt-4">
              <MatchCardSkeleton />
              <MatchCardSkeleton />
              <MatchCardSkeleton />
            </div>
          ) : isScheduleFetchError && !isFakeUser ? (
            <div className="flex items-center justify-center gap-2 text-red-600 bg-red-50 dark:bg-red-950 px-4 py-2 rounded-md text-sm font-medium border border-red-300 dark:border-red-700">
              <ExclamationTriangleIcon className="h-5 w-5" />
              <span>
                {scheduleFetchError?.response?.data?.data?.errorMessage ||
                  scheduleFetchError?.response?.data?.message ||
                  "Error loading schedule."}
              </span>
            </div>
          ) : isFilteredReady &&
            filteredSchedule.length === 0 &&
            !isFakeUser ? (
            <div className="flex items-center justify-center gap-2 text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900 px-4 py-2 rounded-md text-sm font-medium border border-yellow-300 dark:border-yellow-700">
              <ExclamationTriangleIcon className="h-5 w-5" />
              <span>No schedule found for selected filter.</span>
            </div>
          ) : isFakeUser &&
            (!fakeData.schedule || fakeData.schedule.length === 0) ? (
            <div className="flex items-center justify-center gap-2 text-yellow-700 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900 px-4 py-2 rounded-md text-sm font-medium border border-yellow-300 dark:border-yellow-700">
              <ExclamationTriangleIcon className="h-5 w-5" />
              <span>No Schedule Configured</span>
            </div>
          ) : (
            // Live Matches Card Without Live Updates
            <ul className={`${!isFakeUser ? "space-y-6 mt-5" : ""}`}>
              {/*  Live Matches without score*/}
              {(() => {
                const liveData = isFakeUser
                  ? fakeData?.schedule?.live || []
                  : filteredSchedule?.live || [];

                if (liveData.length === 0) return null;

                return (
                  <li>
                    <div className="px-1 md:px-14 w-full text-center">
                      <div
                        className="relative bg-gradient-to-r from-red-900 via-red-700 to-red-900
                      text-red-100 font-semibold uppercase text-sm md:text-base
                      text-center py-2 rounded-t-lg rounded-b-none
                      backdrop-blur-md border border-red-500/40
                      shadow-[0_0_12px_rgba(255,69,0,0.4)] overflow-hidden"
                      >
                        <div className="flex items-center justify-center space-x-2">
                          <div className="h-3 w-3 rounded-full bg-red-400 animate-pulseShadow" />
                          <span>Live Matches</span>
                        </div>

                        <div className="absolute bottom-0 left-0 w-full h-[2px] bg-red-400/20 overflow-hidden">
                          <div className="absolute top-0 left-0 h-full w-[200%] bg-gradient-to-r from-transparent via-red-400 to-transparent animate-scan" />
                        </div>
                      </div>
                    </div>
                    <ul className={`${!isFakeUser ? "space-y-2" : ""}`}>
                      {liveData.map((liveGame, index, arr) => {
                        const isFirst = index === 0;
                        const isLast = index === arr.length - 1;
                        return (
                          <div
                            key={`live-${index}`}
                            className={`${isFirst ? "rounded-t-none" : ""} ${
                              isLast ? "rounded-b-lg" : ""
                            } `}
                          >
                            <UpcomingMatchesCard
                              cardType={"live"}
                              gameData={liveGame}
                              index={index}
                              isFakeUser={isFakeUser}
                              isFirst={isFirst}
                              isLast={isLast}
                            />
                          </div>
                        );
                      })}
                    </ul>
                  </li>
                );
              })()}

              {isFakeUser && (
                <div className="px-1 md:px-14 w-full text-center">
                  <div
                    className="bg-blue-900/70 text-blue-100 font-semibold uppercase text-sm md:text-base
                          text-center py-2 rounded-t-lg rounded-b-none
                          backdrop-blur-md border border-blue-500/40
                          shadow-[0_0_12px_rgba(30,144,255,0.4)]"
                  >
                    Upcoming Lectures
                  </div>
                </div>
              )}

              {/* {!isFakeUser && liveMatches && liveMatches.length > 0 && (
                <li>
                  <div className="px-1 md:px-14 w-full text-center">
                    <div
                      className="relative bg-gradient-to-r from-red-900 via-red-700 to-red-900
                      text-red-100 font-semibold uppercase text-sm md:text-base
                      text-center py-2 rounded-t-lg rounded-b-none
                      backdrop-blur-md border border-red-500/40
                      shadow-[0_0_12px_rgba(255,69,0,0.4)] overflow-hidden"
                    >
                      <div className="flex items-center justify-center space-x-2">
                        <div className="h-3 w-3 rounded-full bg-red-400 animate-pulseShadow" />
                        <span>Live Matches</span>
                      </div>

                      <div className="absolute bottom-0 left-0 w-full h-[2px] bg-red-400/20 overflow-hidden">
                        <div className="absolute top-0 left-0 h-full w-[200%] bg-gradient-to-r from-transparent via-red-400 to-transparent animate-scan" />
                      </div>
                    </div>
                  </div>

                  <ul className="space-y-2">
                    {liveMatches.map((liveGame, index, arr) =>
                      LiveScoreCard(liveGame, index, {
                        isFirst: index === 0,
                        isLast: index === arr.length - 1,
                      })
                    )}
                  </ul>
                </li>
              )} */}

              {/* 🗓 UPCOMING MATCHES SECTION */}
              {Object.entries(
                (isFakeUser
                  ? fakeData.schedule?.upcoming || []
                  : filteredSchedule.upcoming || []
                ).reduce((groups, game) => {
                  const dateKey = dayjs
                    .unix(game.GAME_DATE)
                    .format("YYYY-MM-DD");
                  if (!groups[dateKey]) groups[dateKey] = [];
                  groups[dateKey].push(game);
                  return groups;
                }, {}),
              )
                .sort(([a], [b]) => new Date(a) - new Date(b))
                .map(([dateKey, games]) => {
                  const today = dayjs().startOf("day");
                  const matchDay = dayjs(dateKey);

                  let label;
                  if (matchDay.isSame(today, "day")) label = "Today";
                  else if (matchDay.isSame(today.add(1, "day"), "day"))
                    label = "Tomorrow";
                  else if (matchDay.isSame(today.subtract(1, "day"), "day"))
                    label = "Yesterday";
                  else label = matchDay.format("DD MMMM YYYY");

                  return (
                    <li key={dateKey}>
                      <div className="px-1 md:px-14 w-full text-center">
                        {!isFakeUser ? (
                          <div
                            className="bg-blue-900/70 text-blue-100 font-semibold uppercase text-sm md:text-base
                          text-center py-2 rounded-t-lg rounded-b-none
                          backdrop-blur-md border border-blue-500/40
                          shadow-[0_0_12px_rgba(30,144,255,0.4)]"
                          >
                            {label}
                          </div>
                        ) : (
                          <></>
                          // <div
                          //   className="bg-blue-900/70 text-blue-100 font-semibold uppercase text-sm md:text-base
                          // text-center py-2 rounded-t-lg rounded-b-none
                          // backdrop-blur-md border border-blue-500/40
                          // shadow-[0_0_12px_rgba(30,144,255,0.4)]"
                          // >
                          //   Upcoming
                          // </div>
                        )}
                      </div>

                      <ul className={`${!isFakeUser ? "space-y-2" : ""}`}>
                        {games.map((gameData, index) => {
                          const isFirst = !isFakeUser ? index === 0 : false;
                          const isLast = !isFakeUser
                            ? index === games.length - 1
                            : false;

                          return (
                            <div
                              key={index}
                              className={`${isFirst ? "rounded-t-none" : ""} ${
                                isLast ? "rounded-b-lg" : ""
                              }`}
                            >
                              <UpcomingMatchesCard
                                cardType={"upcoming"}
                                gameData={gameData}
                                isFakeUser={isFakeUser}
                                index={index}
                                isFirst={isFirst}
                                isLast={isLast}
                                isToday={matchDay.isSame(today, "day")}
                              />
                            </div>
                          );
                        })}
                      </ul>
                    </li>
                  );
                })}
            </ul>
          )}
        </div>
      </div>
    </>
  );
};

export default ScheduleWithZoom;
